import React, { Component, ErrorInfo, ReactNode } from 'react'
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { Header2, Header3, PMedium, PSmallFaded } from './Type'
import { Card } from './Cards'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  showRetry?: boolean
  retryText?: string
  context?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  retryCount: number
}

/**
 * Error boundary component that catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire application
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private maxRetries = 3

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    })

    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }))
    }
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <ErrorFallbackUI
          error={this.state.error}
          context={this.props.context}
          onRetry={this.props.showRetry !== false ? this.handleRetry : undefined}
          retryText={this.props.retryText}
          canRetry={this.state.retryCount < this.maxRetries}
          retryCount={this.state.retryCount}
        />
      )
    }

    return this.props.children
  }
}

interface ErrorFallbackUIProps {
  error: Error | null
  context?: string
  onRetry?: () => void
  retryText?: string
  canRetry?: boolean
  retryCount?: number
}

/**
 * Default error fallback UI component
 */
export const ErrorFallbackUI: React.FC<ErrorFallbackUIProps> = ({
  error,
  context,
  onRetry,
  retryText = 'Try Again',
  canRetry = true,
  retryCount = 0
}) => {
  const getErrorMessage = () => {
    if (!error) return 'An unexpected error occurred'

    // Provide user-friendly messages for common errors
    if (error.message.includes('ChunkLoadError') || error.message.includes('Loading chunk')) {
      return 'Failed to load application resources. This might be due to a network issue or an application update.'
    }

    if (error.message.includes('Network Error') || error.message.includes('fetch')) {
      return 'Network connection error. Please check your internet connection and try again.'
    }

    if (error.name === 'TypeError' && error.message.includes('Cannot read properties')) {
      return 'Data loading error. The application encountered an issue while processing data.'
    }

    return error.message || 'An unexpected error occurred'
  }

  const getActionableGuidance = () => {
    if (!error) return null

    if (error.message.includes('ChunkLoadError') || error.message.includes('Loading chunk')) {
      return 'Try refreshing the page. If the problem persists, clear your browser cache.'
    }

    if (error.message.includes('Network Error') || error.message.includes('fetch')) {
      return 'Check your internet connection and try again. If you\'re behind a firewall, contact your IT administrator.'
    }

    return 'If this problem continues, please contact support with the error details below.'
  }

  return (
    <Card className="max-w-2xl mx-auto my-8 p-6">
      <div className="text-center">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />

        <Header2 title={context ? `Error in ${context}` : 'Something went wrong'} />

        <div className="mt-4 space-y-3">
          <PMedium text={getErrorMessage()} />

          {getActionableGuidance() && (
            <PSmallFaded text={getActionableGuidance() || ''} className="text-gray-600" />
          )}

          {retryCount > 0 && (
            <PSmallFaded text={`Retry attempt: ${retryCount}/3`} className="text-gray-500" />
          )}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          {onRetry && canRetry && (
            <button
              onClick={onRetry}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {retryText}
            </button>
          )}

          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Refresh Page
          </button>
        </div>

        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-6 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              Show Error Details (Development)
            </summary>
            <pre className="mt-2 text-xs bg-gray-100 p-3 rounded overflow-auto text-red-600">
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </Card>
  )
}

/**
 * Specialized error boundary for epic-related components
 */
export const EpicErrorBoundary: React.FC<Omit<ErrorBoundaryProps, 'context'>> = (props) => {
  return (
    <ErrorBoundary
      {...props}
      context="Epic Analysis"
      onError={(error, errorInfo) => {
        // Log epic-specific error context
        console.error('Epic component error:', {
          error: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString()
        })

        // Call custom error handler if provided
        if (props.onError) {
          props.onError(error, errorInfo)
        }
      }}
    />
  )
}

/**
 * Higher-order component to wrap components with error boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  )

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`

  return WrappedComponent
}